import React, { useState, useEffect, useRef } from 'react';
import step1card from '../assets/howitworks/HOWITWORKS_CARD_STEP1_mobile.webp';
import step2card from '../assets/howitworks/HOWITWORKS_CARD_STEP2_mobile.webp';
import step3card from '../assets/howitworks/HOWITWORKS_CARD_STEP3_mobile.webp';
import step4card from '../assets/howitworks/HOWITWORKS_CARD_STEP4_mobile.webp';
import step1icon from '../assets/icons/Step1_Icon.svg';
import step2icon from '../assets/icons/Step2_Icon.svg';
import step3icon from '../assets/icons/Step3_Icon.svg';
import step4icon from '../assets/icons/Step4_Icon.svg';

const HowItWorksMobile = () => {
  const [cardWidth, setCardWidth] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateCardWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setCardWidth(containerWidth * 0.85); // 85% of container width
      }
    };

    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  const steps = [
    {
      number: "Analyse",
      title: "Dein Business verstehen",
      description: "Wir starten mit einem strukturierten Deep Dive in deine Abläufe. Von Vertrieb über Backoffice bis zum Kundenservice: Wir identifizieren Bottlenecks, repetitive Aufgaben und ungenutzte Potenziale. Das Ergebnis ist ein klarer Überblick darüber, wo KI wirklich Wirkung entfalten kann. Faktenbasiert, statt nur auf Hypothesen.",
      icon: step1icon,
      image: step1card
    },
    {
      number: "Roadmap",
      title: "Klare KI-Strategie und Proof of Concept",
      description: "Auf Basis der Analyse erstellen wir eine maßgeschneiderte Roadmap: Wir zeigen, welche Prozesse automatisiert werden können, wo Umsatzpotenziale liegen und wie dein Team sofort entlastet wird. Anschließend setzen wir einen Proof of Concept auf, um die Automatisierung fehlerfrei zu testen, bevor sie vollständig implementiert wird.",
      icon: step2icon,
      image: step2card
    },
    {
      number: "Integration",
      title: "KI-Blocks nahtlos implementieren",
      description: "Nach Abschluss der erfolgreichen Testphase, setzen wir die passenden KI-Blocks direkt in deine Systeme ein. Schnell, reibungslos und ohne komplizierte Umstellungen. Alles läuft reibungslos im Hintergrund, ohne dass du bestehende Strukturen auf den Kopf stellen musst. So entsteht Schritt für Schritt dein KI-Stack, der sofort Mehrwert bringt.",
      icon: step3icon,
      image: step3card
    },
    {
      number: "Skalierung",
      title: "Ergebnisse messen und skalieren",
      description: "Nach der Implementierung geht es um Wachstum. Wir messen die erzielten Effekte, optimieren kontinuierlich und erweitern dein Setup um neue KI-Blocks. So stellst du sicher, dass dein Unternehmen nicht nur Kosten spart, sondern auch mehr Kunden gewinnt und nachhaltig skalieren kann.",
      icon: step4icon,
      image: step4card
    }
  ];

  return (
    <section className="relative min-h-[75vh] bg-[#010003] text-white py-20 px-4 pt-[150px] overflow-hidden">
      {/* Blur effect elements */}
      <div className="absolute inset-0 z-0 overflow-visible">
        <div className="absolute bottom-[5%] left-[8%] w-[30vw] h-[50vh] rounded-full bg-[#F5EBEB] opacity-5 blur-3xl"></div>
        <div className="absolute top-[30%] left-[35%] w-[25vw] h-[30vh] rounded-full bg-[#F5EBEB] opacity-5 blur-3xl"></div>
      </div>
      <div className="max-w-[1280px] mx-auto">
        {/* Header Section */}
        <div className="mb-12">
          <h2 className="font-['CalSans'] font-medium text-2xl md:text-4xl text-white tracking-[-0.02em] text-left mb-4">
            Beratung, die in<br className="md:hidden"/> Handlung umsetzt.
          </h2>
          <p className="font-['Geist'] font-normal text-sm md:text-base text-[#ADB0B9] max-w-[700px] leading-relaxed">
            Keine endlosen Reports, sondern direkte Umsetzung. Wir verbinden strategische Beratung mit technischer Umsetzung. So weißt du nicht nur, was möglich ist, sondern siehst schnell echte Resultate in deinem Tagesgeschäft.
          </p>
        </div>

        {/* Mobile Cards */}
        <div className="relative z-10 -mt-4">
          <div 
            ref={containerRef}
            className="flex overflow-x-auto pb-6 -mx-4 px-4 no-scrollbar"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="flex-shrink-0 relative overflow-hidden"
                style={{
                  width: `${cardWidth}px`,
                  marginRight: index < steps.length - 1 ? '1.5rem' : '0',
                  scrollSnapAlign: 'start',
                }}
              >
                <div 
                  className="rounded-2xl w-full h-[350px] relative overflow-hidden"
                  style={{
                    backgroundImage: `url(${step.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Semi-transparent overlay */}
                  <div className="absolute inset-0 bg-black/20"></div>
                  
                  {/* Top Left Icon */}
                  <div className="absolute top-5 left-5 z-10">
                    <img 
                      src={step.icon} 
                      alt={`Step ${index + 1}`}
                      className="w-10 h-10"
                      loading="lazy"
                    />
                  </div>

                  {/* Vertical Line */}
                  <div className="absolute top-0 bottom-0 left-20 w-px bg-white/25"></div>

                  {/* Vertical Text (Step Number) */}
                  <div className="absolute bottom-5 left-5 w-6 h-32 overflow-visible z-10">
                    <span className="absolute bottom-0 left-0 font-['CalSans'] text-[22px] font-normal tracking-[0.02em] text-white transform -rotate-90 origin-bottom-left whitespace-nowrap translate-x-10 translate-y-0">
                      {step.number}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="absolute top-8 left-[100px] right-5 z-10">
                    <h3 className="font-['Geist'] font-normal text-[16px] text-white tracking-[-0.02em] mb-3">
                      {step.title}
                    </h3>
                    <p className="font-['Geist'] font-normal text-[13px] text-[#787c87] tracking-[-0.02em] pr-4">
                      {step.description}
                    </p>
                  </div>

                  {/* Bottom Right Text */}
                  <div className="absolute bottom-5 right-5 w-[calc(100%-100px)] z-10">
                    <p className="font-['Geist'] font-normal text-[10px] text-white/50 text-right tracking-[0.01em]">
                      {index === 0 ? '25%' : index === 1 ? '50%' : index === 2 ? '75%' : '100%'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksMobile;